<script lang="ts">
	import { onMount } from 'svelte';
	import { getOptimizedImageSet, getBlurHash } from './performantImage';

	export let image_url: string;
	export let alt = '...';
	export let borderClass: string = 'rounded-t-2xl';

	let imageLoaded = false;
	let imageError = false;
	let containerWidth: number;
	let imageRef: HTMLImageElement;
	let resizeObserver: ResizeObserver | null = null;

	const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

	$: optimizedImage =
		containerWidth && image_url ? getOptimizedImageSet(image_url) : { src: '', srcset: '' };

	$: blurHashUrl = image_url ? getBlurHash(image_url) : '';

	// Reactive statement to handle imageRef changes
	$: if (imageRef && resizeObserver && imageRef instanceof Element) {
		try {
			resizeObserver.observe(imageRef);
		} catch (error) {
			console.warn('Failed to observe image element in reactive statement:', error);
			// Fallback: set a default width if we don't have one
			if (!containerWidth) {
				containerWidth = 300; // Default to tablet size
			}
		}
	}

	function handleImageError() {
		console.log('Image error for:', image_url);
		imageError = true;
		imageLoaded = true;
	}

	onMount(() => {
		// Check if ResizeObserver is supported
		if (typeof ResizeObserver === 'undefined') {
			console.warn('ResizeObserver not supported, using fallback');
			// Fallback: set a default width
			containerWidth = 300; // Default to tablet size
			return;
		}

		// Use a more efficient ResizeObserver with reduced delay
		resizeObserver = new ResizeObserver((entries) => {
			// Use requestAnimationFrame for better performance
			requestAnimationFrame(() => {
				if (entries[0]) {
					containerWidth = entries[0].contentRect.width;
				}
			});
		});

		// Function to observe the image element
		const observeImage = () => {
			if (imageRef && imageRef instanceof Element && resizeObserver) {
				try {
					resizeObserver.observe(imageRef);
				} catch (error) {
					console.warn('Failed to observe image element:', error);
					// Fallback: set a default width
					containerWidth = 300; // Default to tablet size
				}
			}
		};

		// Try to observe immediately, and also set up a fallback
		observeImage();

		// If imageRef is not available immediately, try again after a short delay
		if (!imageRef) {
			setTimeout(observeImage, 100);
		}

		// --- Fix hydration mismatch: if image is already loaded, set imageLoaded = true ---
		if (imageRef && imageRef.complete) {
			imageLoaded = true;
		}

		return () => {
			if (resizeObserver) {
				resizeObserver.disconnect();
				resizeObserver = null;
			}
		};
	});
</script>

<div class={`relative w-full aspect-[16/9] overflow-hidden ${borderClass}`}>
	{#if !imageLoaded && !imageError && image_url}
		<img
			src={blurHashUrl}
			alt=""
			class="absolute inset-0 w-full h-full object-cover rounded-t-2xl bg-cosmicPurplebg transition-opacity duration-100"
			class:opacity-0={imageLoaded}
			width="300"
			height="169"
		/>
	{/if}

	{#if image_url}
		<picture>
			<img
				bind:this={imageRef}
				loading="lazy"
				decoding="async"
				class="absolute inset-0 w-full h-full object-cover rounded-t-2xl bg-cosmicPurplebg transition-opacity duration-100"
				class:opacity-0={!imageLoaded}
				class:opacity-100={imageLoaded}
				src={optimizedImage.src || TRANSPARENT_PIXEL}
				srcset={optimizedImage.srcset}
				sizes="(max-width: 256px) 256px,
					   (max-width: 300px) 300px,
					   320px"
				{alt}
				width="300"
				height="169"
				on:load={() => {
					imageLoaded = true;
				}}
				on:error={handleImageError}
			/>
		</picture>
	{:else}
		<div
			class="absolute inset-0 w-full h-full bg-cosmicPurplebg flex items-center justify-center rounded-t-2xl"
		>
			<span class="text-gray-400">No image available</span>
		</div>
	{/if}
</div>
